import { useCallback, useEffect, useMemo, useRef } from "react";

export type DebouncedCallback<TArgs extends unknown[]> = {
  /** Schedules `fn` with these arguments, replacing any pending call. */
  debounced: (...args: TArgs) => void;
  /** Drops the pending call, if any. */
  cancel: () => void;
  /** Runs the pending call now, if any. */
  flush: () => void;
};

/**
 * A trailing-edge debounce over `fn`. The returned functions are stable for
 * the hook's lifetime; `fn` is read through a ref, so the latest closure
 * runs when the timer fires. A pending call is dropped on unmount: a caller
 * whose last write must land calls `flush` in its own unmount effect.
 */
export function useDebouncedCallback<TArgs extends unknown[]>(
  fn: (...args: TArgs) => void,
  delayMs: number,
): DebouncedCallback<TArgs> {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingArgs = useRef<TArgs | null>(null);

  const cancel = useCallback(() => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
    pendingArgs.current = null;
  }, []);

  const flush = useCallback(() => {
    const args = pendingArgs.current;
    cancel();
    if (args !== null) fnRef.current(...args);
  }, [cancel]);

  const debounced = useCallback(
    (...args: TArgs) => {
      if (timer.current !== null) clearTimeout(timer.current);
      pendingArgs.current = args;
      timer.current = setTimeout(flush, delayMs);
    },
    [delayMs, flush],
  );

  useEffect(() => cancel, [cancel]);

  return useMemo(() => ({ debounced, cancel, flush }), [debounced, cancel, flush]);
}
